// Utility functions for payment calculations
import { getServiceType, calculateBillableHours, isLocationRefusal } from './serviceTypeUtils';

/**
 * Determines the unit rate based on provider's rate table
 * @param {Object} provider - The provider object with rate information
 * @param {number} unitCount - The number of units
 * @returns {number} The appropriate rate for the given unit count
 */
export const determineUnitRate = (provider, unitCount) => {
  if (unitCount <= 0) return 0;
  
  // Cap at 10 units for rate lookup
  const lookupCount = Math.min(unitCount, 10);
  
  // Get the appropriate rate field
  const rateField = lookupCount === 1 
    ? 'rate_1_unit' 
    : `rate_${lookupCount}_units`;
  
  // Ensure we return a number, not a string
  const rate = provider[rateField];
  return rate ? Number(rate) : 0;
};

/**
 * Determines the unit rate from provider_service_rates table (new structure)
 * @param {Object} providerServiceRate - Provider service rate configuration object
 * @param {number} unitCount - The number of units
 * @returns {number} The appropriate rate for the given unit count
 */
export const determineUnitRateFromServiceRate = (providerServiceRate, unitCount) => {
  if (unitCount <= 0) return 0;
  
  // Cap at 10 units for rate lookup
  const lookupCount = Math.min(unitCount, 10);
  
  // Get the appropriate rate field
  const rateField = lookupCount === 1 
    ? 'rate_1_unit' 
    : `rate_${lookupCount}_units`;
  
  // Ensure we return a number, not a string
  const rate = providerServiceRate[rateField];
  return rate ? Number(rate) : 0;
};

/**
 * Calculates the cost for a location based on provider-specific service rate configuration
 * @param {Object} provider - The provider object with rate information
 * @param {number} unitCount - The number of units
 * @param {boolean} isRefusal - Whether this is a refusal calculation
 * @param {string} serviceTypeName - The name of the service type (optional)
 * @param {number} billableHours - The number of billable hours for hourly work (optional)
 * @param {Object} providerServiceRate - Provider's specific rate config for this service type (optional)
 * @returns {Promise<number>} The calculated cost for the location
 */
export const calculateLocationCost = async (provider, unitCount, isRefusal, serviceTypeName = null, billableHours = 0, providerServiceRate = null) => {
  if (!provider) return 0;
  
  // Get the service type (defaults to Initial Service Call if not provided)
  const serviceType = await getServiceType(serviceTypeName);
  
  // If we have provider-specific service rate configuration, use it
  if (providerServiceRate) {
    if (providerServiceRate.rate_type === 'HOURLY') {
      // Hourly billing based on provider's rate for this service
      const hourlyRate = providerServiceRate.hourly_rate || 0;
      if (!hourlyRate) {
        console.warn(`Provider ${provider.name} has no hourly rate defined for service type ${serviceTypeName}`);
        return 0;
      }
      if (billableHours <= 0) {
        console.warn(`Provider ${provider.name} - ${serviceTypeName}: HOURLY rate configured but billableHours is ${billableHours}`);
        return 0;
      }
      const cost = Number(hourlyRate * billableHours);
      console.log(`[HOURLY CALC] Provider: ${provider.name}, Service: ${serviceTypeName}, Rate: $${hourlyRate}/hr, Hours: ${billableHours}, Cost: $${cost.toFixed(2)}`);
      return cost;
    } else {
      // Flat rate billing based on provider's rate for this service
      if (isRefusal) {
        const refusalRate = providerServiceRate.refusal_rate || 0;
        return Number(refusalRate * unitCount);
      } else {
        // Check if this is Initial Service Call with tiered structure
        if (serviceType.name === 'Initial Service Call') {
          // Use tiered rate logic from provider_service_rates
          const hasTieredRates = providerServiceRate.per_board || 
                                 providerServiceRate.per_location || 
                                 providerServiceRate.rate_1_unit ||
                                 providerServiceRate.rate_2_units ||
                                 providerServiceRate.rate_3_units;
          
          if (hasTieredRates) {
            let locationCost = 0;
            
            // If provider has a per_board rate, use that
            if (providerServiceRate.per_board !== null && providerServiceRate.per_board !== undefined) {
              const perBoard = Number(providerServiceRate.per_board);
              locationCost = perBoard * unitCount;
              
              // Add per location fee when using per_board rate
              const perLocationFee = providerServiceRate.per_location ? Number(providerServiceRate.per_location) : 0;
              locationCost += perLocationFee;
            } else {
              // Use unit-based rate from provider_service_rates
              const unitRate = determineUnitRateFromServiceRate(providerServiceRate, unitCount);
              locationCost = unitRate || 0;
              
              // For Initial Service Call with tiered rates, do NOT add per_location 
              // unless using per_board (already handled above)
            }
            
            return Number(locationCost);
          }
        }
        
        // For non-tiered or non-Initial Service Call, use simple flat rate
        const flatRate = providerServiceRate.flat_rate || 0;
        return Number(flatRate * unitCount);
      }
    }
  }
  
  // LEGACY PATH: Fallback to old provider table structure (for backward compatibility)
  // This path should eventually be deprecated once all providers have service rate configs
  
  // For hourly service types (using old structure)
  if (serviceType.rate_type === 'HOURLY') {
    // Use provider's hourly rate if available
    const hourlyRate = provider.hourly_rate || 0;
    if (!hourlyRate) {
      console.warn(`Provider ${provider.name} has no hourly rate defined for hourly service type ${serviceType.name}`);
      return 0;
    }
    return Number(hourlyRate * billableHours);
  }
  
  // For flat rate service types (existing logic)
  // Ensure unit count is a number
  unitCount = Number(unitCount);
  if (isNaN(unitCount) || unitCount <= 0) return 0;
  
  let locationCost = 0;
  
  if (isRefusal) {
    // For refusal files, we use the refusal rate
    if (provider.rate_refusal !== undefined && provider.rate_refusal !== null) {
      // Ensure rate_refusal is a number
      const refusalRate = Number(provider.rate_refusal);
      locationCost = refusalRate * unitCount;
    }
  } else {
    // Regular calculation for completed work
    // Special rule: Ruffle Feathers must use ONLY the 1-Unit column as a flat per-location payout
    if (serviceType.name === 'Ruffle Feathers') {
      const oneUnitRate = Number(provider.rate_1_unit) || 0;
      // Do NOT multiply by unitCount. Do NOT use per_board or tiered columns.
      // Do NOT add per_location unless explicitly required (defaulting to not adding).
      locationCost = oneUnitRate;
    } else {
      // Standard logic for other flat-rate service types
      // Get rate based on unit count
      const unitRate = determineUnitRate(provider, unitCount);
      
      // If provider has a per_board rate, use that instead of the unit rate
      if (provider.per_board !== null && provider.per_board !== undefined) {
        // Per unit rate calculation - ensure per_board is a number
        const perBoard = Number(provider.per_board);
        locationCost = perBoard * unitCount;
        
        // Add per location fee when using per_board rate
        const perLocationFee = provider.per_location ? Number(provider.per_location) : 0;
        locationCost += perLocationFee;
      } else {
        // Flat rate calculation - unit rates already include location fees
        locationCost = unitRate || 0;
        
        // For Initial Service Call, do NOT add per_location fee when using unit rates
        // as the unit rate is the complete rate for that service type
        if (serviceType.name !== 'Initial Service Call') {
          // For other service types, add per location fee if applicable
          const perLocationFee = provider.per_location ? Number(provider.per_location) : 0;
          locationCost += perLocationFee;
        }
      }
    }
  }
  
  // Ensure the final result is a number
  return Number(locationCost);
};

/**
 * Validates if a provider has all necessary rate information
 * @param {Object} provider - The provider object to validate
 * @param {string} serviceTypeName - The service type name (optional)
 * @returns {Promise<Object>} Validation result with isValid flag and error message
 */
export const validateProviderRates = async (provider, serviceTypeName = null) => {
  if (!provider) {
    return { isValid: false, error: 'Provider not found' };
  }
  
  // Get the service type
  const serviceType = await getServiceType(serviceTypeName);
  
  // For hourly service types, check hourly rate
  if (serviceType.rate_type === 'HOURLY') {
    if (!provider.hourly_rate || provider.hourly_rate <= 0) {
      return {
        isValid: false,
        error: `Provider ${provider.name} needs hourly rate defined for hourly service type ${serviceType.name}`
      };
    }
    return { isValid: true };
  }
  
  // For flat rate service types (existing validation)
  // Check for refusal rate
  if (provider.rate_refusal === null || provider.rate_refusal === undefined) {
    return { 
      isValid: false, 
      error: `Missing refusal rate for provider: ${provider.name}` 
    };
  }
  
  // Check if either per_board or unit rates are defined
  const hasPerBoard = provider.per_board !== null && provider.per_board !== undefined;
  const hasUnitRates = provider.rate_1_unit !== null && provider.rate_1_unit !== undefined;
  
  if (!hasPerBoard && !hasUnitRates) {
    return { 
      isValid: false, 
      error: `Provider ${provider.name} needs either per_board rate or unit rates defined` 
    };
  }
  
  return { isValid: true };
};

/**
 * Extract service type from CSV row data
 * @param {Object} row - The CSV row data
 * @param {Object} sampleRow - A sample row to help find column names
 * @returns {string|null} The service type name or null
 */
export const extractServiceType = (row, sampleRow) => {
  // Try to find service type column with various names
  const possibleColumns = [
    'Service Type',
    'SERVICE TYPE',
    'service type',
    'Type',
    'TYPE',
    'Service',
    'SERVICE'
  ];
  
  // Find the actual column name
  const columnName = Object.keys(sampleRow).find(key => 
    possibleColumns.some(possible => 
      key.toLowerCase() === possible.toLowerCase() ||
      key.toLowerCase().includes('service') && key.toLowerCase().includes('type')
    )
  );
  
  if (columnName && row[columnName]) {
    return row[columnName].toString().trim();
  }
  
  return null;
};

/**
 * Extract timestamp data from CSV row
 * @param {Object} row - The CSV row data
 * @param {Object} sampleRow - A sample row to help find column names
 * @returns {Object} Timestamp data for billable hours calculation
 */
export const extractTimestamps = (row, sampleRow) => {
  const timestamps = {
    travel_time_start: null,
    travel_time_end: null,
    arrival_time: null,
    departure_time: null,
    billable_hours: null,
    // Additional optional fields: explicit minutes when provided
    onsite_minutes: null,
    travel_minutes: null
  };
  
  // Helper to normalize header keys (tolerate spaces, underscores, case, punctuation)
  const normalize = (s) => s
    .toString()
    .toLowerCase()
    .replace(/[_\-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/[^a-z0-9 ()]/g, '')
    .trim();

  // Define possible column names for each timestamp (normalized forms)
  const timestampMappings = {
    travel_time_start: ['travel time start', 'travel start', 'traveltime start', 'travel_time_start', 'travel time start (utc)', 'travel time start utc'],
    travel_time_end: ['travel time end', 'travel end', 'traveltime end', 'travel_time_end', 'travel time end (utc)', 'travel time end utc'],
    // Map geofence columns per user request (and a few tolerant variants)
    arrival_time: ['arrival time', 'arrival', 'onsite start', 'geofence entered', 'geofence_entered'],
    departure_time: ['departure time', 'departure', 'onsite end', 'geofence exited', 'geofence_exited', 'geofence exit'],
    billable_hours: ['billable hours', 'hours', 'total hours', 'billable_hours']
  };
  
  // Find actual column names and extract values
  Object.keys(timestampMappings).forEach(field => {
    const possibleColumns = timestampMappings[field];
    const columnName = Object.keys(row).find(key => {
      const nk = normalize(key);
      return possibleColumns.some(possible => nk === normalize(possible));
    });
    
    if (columnName && row[columnName]) {
      const value = row[columnName];
      // For billable hours, parse as number
      if (field === 'billable_hours') {
        timestamps[field] = parseFloat(value) || null;
      } else {
        // For timestamps, keep as string for parsing later
        timestamps[field] = value.toString().trim();
      }
    }
  });
  
  // Handle explicit onsite minutes columns
  const onsiteMinutesColumns = [
    'Time On Site(Mins)',
    'TIME ON SITE(MINS)',
    'TIME ON SITE (MINS)',
    'Onsite Minutes',
    'ONSITE MINUTES',
    'time on site mins',
    'time_on_site_mins'
  ];
  const onsiteMinutesCol = Object.keys(row).find(key => {
    const nk = normalize(key);
    return onsiteMinutesColumns.some(possible => nk === normalize(possible));
  });
  if (onsiteMinutesCol && row[onsiteMinutesCol] !== undefined && row[onsiteMinutesCol] !== null) {
    const mins = parseFloat(row[onsiteMinutesCol]);
    if (!isNaN(mins) && mins >= 0) {
      timestamps.onsite_minutes = mins;
    }
  }

  // Handle explicit travel time minutes columns
  const travelMinutesColumns = [
    'Travel Time(Mins)',
    'TRAVEL TIME(MINS)',
    'TRAVEL TIME (MINS)',
    'Travel Time (Mins)',
    'Travel Minutes',
    'TRAVEL MINUTES',
    'travel time mins',
    'travel_time_mins',
    'Total Travel Time(Mins)',
    'TOTAL TRAVEL TIME(MINS)',
    'Total Travel Time (Mins)',
    'total travel time mins',
    'total_travel_time_mins'
  ];
  const travelMinutesCol = Object.keys(row).find(key => {
    const nk = normalize(key);
    return travelMinutesColumns.some(possible => nk === normalize(possible));
  });
  if (travelMinutesCol && row[travelMinutesCol] !== undefined && row[travelMinutesCol] !== null) {
    const mins = parseFloat(row[travelMinutesCol]);
    if (!isNaN(mins) && mins >= 0) {
      timestamps.travel_minutes = mins;
    }
  }

  return timestamps;
};

/**
 * Calculate Coca-Cola revenue based on service type
 * @param {string} serviceTypeName - The service type name
 * @param {boolean} isRefusal - Whether this is a refusal
 * @param {number} unitCount - Number of units (for flat rate)
 * @param {number} billableHours - Number of hours (for hourly rate)
 * @returns {Promise<number>} The revenue amount
 */
export const calculateCocaColaRevenue = async (serviceTypeName, isRefusal, unitCount = 1, billableHours = 0) => {
  const serviceType = await getServiceType(serviceTypeName);
  
  if (serviceType.rate_type === 'HOURLY') {
    return (serviceType.hourly_rate || 0) * billableHours;
  } else {
    // Flat rate
    if (isRefusal) {
      return (serviceType.flat_refusal_rate || 0) * unitCount;
    } else {
      return (serviceType.flat_completion_rate || 0) * unitCount;
    }
  }
}; 